import {
  AlertCircle,
  Ban,
  BadgeCheck,
  Camera,
  Check,
  CheckCircle2,
  Clock,
  Loader2,
  MessageSquare,
  RefreshCw,
  X,
  XCircle,
} from 'lucide-react'
import { AuthedThumb } from '../../components/AuthedImage'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ApiError, apiErrorMessage, getAdminToken, setAdminToken } from '../../api/client'
import { roleLabel } from '../../auth/roleOptions'
import { AdminHeader } from './adminAccess'
import { adminApi } from '../../api/endpoints'
import { ErrorMessage } from '../../components/ErrorMessage'
import type { VerificationReviewResponse, VerificationStatus } from '../../types/api'

const TABS: { label: string; status?: VerificationStatus; rejected?: boolean; icon: ReactNode; active: string }[] = [
  // Every provider not yet reviewed: tutors no longer submit documents, the admin verifies them here.
  { label: 'To verify', status: undefined, icon: <Clock size={14} />, active: 'border-amber-400 bg-amber-100 text-amber-900' },
  { label: 'Verified', status: 'VERIFIED', icon: <BadgeCheck size={14} />, active: 'border-emerald-400 bg-emerald-100 text-emerald-900' },
  // Turned down by an admin - still UNVERIFIED, so they stay listed without the badge.
  { label: 'Rejected', rejected: true, icon: <XCircle size={14} />, active: 'border-rose-400 bg-rose-100 text-rose-900' },
  { label: 'Suspended', status: 'SUSPENDED', icon: <Ban size={14} />, active: 'border-slate-400 bg-slate-200 text-slate-900' },
]

/** DOCS_DECLARED reads as unverified to ops - see AdminProvidersPage. */
const STATUS_PILL: Record<string, { cls: string; label: string }> = {
  VERIFIED: { cls: 'bg-emerald-100 text-emerald-800', label: 'Verified' },
  UNVERIFIED: { cls: 'bg-amber-100 text-amber-800', label: 'Unverified' },
  DOCS_DECLARED: { cls: 'bg-amber-100 text-amber-800', label: 'Unverified' },
  SUSPENDED: { cls: 'bg-rose-100 text-rose-800', label: 'Suspended' },
}

export function AdminVerificationsPage() {
  const navigate = useNavigate()
  const [tab, setTab] = useState(0)
  const [rows, setRows] = useState<VerificationReviewResponse[]>([])
  const [notes, setNotes] = useState<Record<string, string>>({})
  const [busyId, setBusyId] = useState<string | null>(null)
  // Revealed numbers are held only for this page view - never written to storage, and gone on
  // refresh, so a shared ops machine does not keep them on screen indefinitely.
  const [error, setError] = useState<string | null>(null)
  // A decision that could not be made, shown on that provider's card beside its buttons.
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null)
  // Each card's note box, so a rejection without a reason can put the cursor straight in it.
  const noteInputs = useRef<Record<string, HTMLInputElement | null>>({})
  const [notice, setNotice] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const signOut = useCallback(() => {
    setAdminToken(null)
    navigate('/admin/login', { replace: true })
  }, [navigate])

  const load = useCallback(() => {
    setLoading(true)
    adminApi
      .queue(TABS[tab].status, TABS[tab].rejected)
      .then(setRows)
      .catch((err) => {
        // An expired or revoked ops token should land on the sign-in page, not on an error the
        // admin can do nothing about.
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          signOut()
          return
        }
        setError(apiErrorMessage(err, 'Could not load the queue'))
      })
      .finally(() => setLoading(false))
  }, [tab, signOut])

  useEffect(() => {
    if (getAdminToken()) load()
  }, [load])

  if (!getAdminToken()) return <Navigate to="/admin/login" replace />

  async function decide(row: VerificationReviewResponse, approve: boolean) {
    const note = (notes[row.participantId] ?? '').trim()
    if (!approve && !note) {
      setRowError({ id: row.participantId, message: 'A rejection needs a reason - the provider has nothing to fix without one.' })
      noteInputs.current[row.participantId]?.focus()
      return
    }
    setRowError(null)
    setBusyId(row.participantId)
    try {
      const body = { note: note || undefined }
      if (approve) await adminApi.approve(row.participantId, body)
      else await adminApi.reject(row.participantId, body)
      setNotice(
        approve
          ? `${row.fullName} is verified - the Verified badge now shows on their listing.`
          : `${row.fullName} was turned down and moved to Rejected. They stay listed, without the Verified badge.`,
      )
      // A decision always moves the provider to another tab (Verified or Rejected), so the card
      // leaves this one - rather than re-fetching the whole list.
      setRows((prev) => prev.filter((r) => r.participantId !== row.participantId))
    } catch (err) {
      setRowError({ id: row.participantId, message: apiErrorMessage(err, 'Could not save that decision') })
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="admin-console min-h-screen bg-ink-100 lg:pl-64">
      <AdminHeader current="verifications" onSignOut={signOut} width="max-w-5xl">
        <button onClick={load} aria-label="Refresh" className="btn-ghost gap-1.5">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </AdminHeader>

      <main className="mx-auto max-w-5xl px-4 py-6">
        <h1 className="text-2xl font-extrabold text-ink-900">Provider verification</h1>
        <p className="mt-0.5 text-sm text-ink-500">
          Approving a provider gives them the Verified badge on the map, the list and their profile.
        </p>

        <div className="mt-4 flex flex-wrap gap-2">
          {TABS.map((t, i) => {
            const active = i === tab
            return (
              <button
                key={t.label}
                onClick={() => setTab(i)}
                aria-pressed={active}
                className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-semibold transition ${
                  active ? t.active : 'border-ink-200 bg-white text-ink-600 hover:border-ink-300 hover:text-ink-900'
                }`}
              >
                {t.icon} {t.label}
                {active && !loading && (
                  <span className="rounded-full bg-white/70 px-1.5 text-[11px] font-bold">{rows.length}</span>
                )}
              </button>
            )
          })}
        </div>

        {notice && (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
            {notice}
          </div>
        )}
        <div className="mt-4">
          <ErrorMessage message={error} />
        </div>

        {loading ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-ink-500">
            <Loader2 size={15} className="animate-spin" /> Loading...
          </div>
        ) : rows.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-ink-200/70 bg-white p-5 text-sm text-ink-500 shadow-card">
            Nothing here. Every provider who is not verified yet appears under{' '}
            <strong>To verify</strong>.
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {rows.map((row) => {
              const st = STATUS_PILL[row.verificationStatus] ?? STATUS_PILL.UNVERIFIED
              return (
                <div key={row.participantId} className="rounded-2xl border border-ink-200/70 bg-white p-5 shadow-card">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-lg font-bold text-ink-900">{row.fullName}</span>
                    <span className="pill bg-ink-100 text-ink-700">{roleLabel(row.entityKind)}</span>
                    <span className={`pill ${st.cls}`}>{st.label}</span>
                    <span className="pill bg-indigo-50 text-indigo-700">{row.verificationTier}</span>
                    {/* Tutors, schools and colleges upload 3 to 5 photos to be verified by. */}
                    {row.photosRequired && (
                      <span
                        className={`pill gap-1 ${
                          row.photoIds.length >= 3 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        <Camera size={12} /> Photos {row.photoIds.length}/{row.photoIds.length >= 3 ? 5 : 3}
                      </span>
                    )}
                  </div>

                  <dl className="mt-3 grid grid-cols-1 gap-x-8 gap-y-1.5 text-sm sm:grid-cols-2">
                    <Row label="Email" value={row.email} />
                    <Row label="Mobile" value={row.phone} />
                    <Row label="Pincode" value={row.pincode} />
                    <Row label="City" value={[row.locality, row.city].filter(Boolean).join(', ')} />
                    <Row label="Service" value={row.serviceType} />
                    <Row
                      label="Experience"
                      value={row.yearsOfExperience != null ? `${row.yearsOfExperience} yrs` : undefined}
                    />
                    <Row label="Subjects" value={row.subjects.join(', ')} />

                    {/* An institution is reviewed on a public registry code and an address,
                        not on a person's ID document - so the whole ID block is replaced
                        rather than left empty beside it. */}
                    {row.institution ? (
                      <>
                        <Row label="Type" value={row.institution.institutionType} />
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <dt className="text-ink-500">{row.institution.registryKind ?? 'Registry'} code</dt>
                          <dd className="min-w-0 break-words font-mono font-medium text-ink-900">
                            {row.institution.registryCode}
                          </dd>
                        </div>
                        <Row
                          label="Coordinator"
                          value={[row.institution.coordinatorName, row.institution.coordinatorDesignation]
                            .filter(Boolean)
                            .join(' · ')}
                        />
                        <Row label="Address" value={row.institution.address} />
                        <Row label="Landmark" value={row.institution.landmark} />
                        <Row label="Established" value={row.institution.yearEstablished?.toString()} />
                        {row.institution.website && (
                          <div className="flex flex-wrap items-baseline gap-x-2">
                            <dt className="text-ink-500">Website</dt>
                            <dd className="min-w-0 break-words font-medium">
                              <a
                                href={row.institution.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-ink-900 underline underline-offset-2"
                              >
                                {row.institution.website}
                              </a>
                            </dd>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <Row label="ID declared" value={row.idType} />
                        <Row label="Address declared" value={row.addressDeclared} />
                      </>
                    )}
                  </dl>

                  {row.bio && (
                    <p className="mt-3 border-l-2 border-sun-400 pl-3 text-sm italic text-ink-600">{row.bio}</p>
                  )}

                  {row.photosRequired && (
                    <div className="mt-4">
                      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-500">
                        <Camera size={13} /> Verification photos
                      </div>
                      {row.photoIds.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {row.photoIds.map((id, i) => (
                            <AuthedThumb
                              key={id}
                              load={() => adminApi.verificationPhoto(row.participantId, id)}
                              imageKey={id}
                              alt={`${row.fullName} - photo ${i + 1}`}
                            />
                          ))}
                        </div>
                      ) : (
                        <p className="mt-1.5 text-sm text-amber-700">No photos uploaded yet.</p>
                      )}
                    </div>
                  )}

                  {row.verificationNote && (
                    <p className="mt-3 flex items-start gap-1.5 text-xs text-ink-500">
                      <MessageSquare size={13} className="mt-px shrink-0" />
                      <span>
                        Last note: {row.verificationNote}
                        {row.verifiedBy ? ` - ${row.verifiedBy}` : ''}
                      </span>
                    </p>
                  )}

                  <div className="-mx-5 -mb-5 mt-4 space-y-2 rounded-b-2xl border-t border-ink-100 bg-ink-50/60 px-5 py-4">
                    <label className="block">
                      <span className="label">
                        {/* Shown to the provider, so a rejection needs one; an approval does not. */}
                        Decision note {TABS[tab].rejected ? '(optional)' : '(required to reject, optional to approve)'}
                      </span>
                      <input
                        ref={(el) => {
                          noteInputs.current[row.participantId] = el
                        }}
                        aria-invalid={rowError?.id === row.participantId}
                        value={notes[row.participantId] ?? ''}
                        onChange={(e) => {
                          setNotes((prev) => ({ ...prev, [row.participantId]: e.target.value }))
                          if (rowError?.id === row.participantId) setRowError(null)
                        }}
                        className={`input !bg-white ${
                          rowError?.id === row.participantId ? '!border-red-400 ring-2 ring-red-100 focus:!ring-red-200' : ''
                        }`}
                        placeholder="Required to reject - e.g. ID photo unreadable"
                      />
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Approving someone already verified changes nothing, so it is not offered. */}
                      {row.verificationStatus !== 'VERIFIED' && (
                        <button
                          onClick={() => decide(row, true)}
                          disabled={busyId === row.participantId}
                          className="btn-sun gap-1.5"
                        >
                          <Check size={15} /> Approve
                        </button>
                      )}
                      {/* Already turned down - rejecting again changes nothing, so only Approve is offered. */}
                      {!TABS[tab].rejected && (
                        <button
                          onClick={() => decide(row, false)}
                          disabled={busyId === row.participantId}
                          className="btn-ghost gap-1.5 !border-rose-200 !text-rose-700 hover:!bg-rose-50"
                        >
                          <X size={15} /> Reject
                        </button>
                      )}
                      {rowError?.id === row.participantId && (
                        <span
                          role="alert"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700"
                        >
                          <AlertCircle size={15} className="shrink-0" />
                          {rowError.message}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </main>
    </div>
  )
}

function Row({ label, value }: { label: string; value?: string }) {
  if (!value) return null
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="text-ink-500">{label}</dt>
      <dd className="min-w-0 break-words font-medium text-ink-900">{value}</dd>
    </div>
  )
}
